import {Component, Inject, OnInit} from '@angular/core';
import { languages } from '@shared/helpers';
import {TranslateService} from '@ngx-translate/core';
import {LocalizeRouterService} from '@gilsdav/ngx-translate-router';
import {CookieService} from 'ngx-cookie';
import {Select} from '@ngxs/store';
import {combineLatest, Observable} from 'rxjs';
import {ICompany, IUser} from '@interfaces/user';
import {ISubscription} from '@interfaces/subscription-plan';
import {UserSelectors} from '@store/user';
import {PaymentSelectors} from '@store/payment';
import {AuthService} from '@services/auth/auth.service';
import {UserRole} from '@shared/enums/user-roles.enum';
import {DOCUMENT} from '@angular/common';
import {map} from "rxjs/operators";
import {environment} from "@env/environment";
import {Ability} from '@casl/ability';
import {Router} from '@angular/router';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
  @Select(UserSelectors.getCompanyData) company$!: Observable<ICompany>;
  @Select(PaymentSelectors.getUserSubscription) subscription$!: Observable<ISubscription>;
  @Select(UserSelectors.getUser) user$!: Observable<IUser>;

  public languages: {label: string, value: string}[] = languages;
  public lang: string;
  public userRole = UserRole;
  public env = environment;

  constructor(
    private translate: TranslateService,
    private localize: LocalizeRouterService,
    private cookie: CookieService,
    private router: Router,
    private authService: AuthService,
    private ability: Ability,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.lang = translate.currentLang;
  }

  public setLang(lang: string): void {
    this.lang = lang;
    this.cookie.put('dWLang', lang);

    if (lang === this.localize.parser.currentLang) {
      this.translate.use(lang);
      return;
    }

    const localizedUrl = this.buildLocalizedUrl(this.router.url, lang);

    this.localize.parser.translateRoutes(lang).subscribe(() => {
      this.router.resetConfig(this.deepCopy(this.localize.parser.routes));
      this.router.navigateByUrl(localizedUrl);
    });
  }

  private buildLocalizedUrl(url: string, lang: string): string {
    const urlParts = /^([^?#]*)(.*)$/.exec(url);
    const suffix = urlParts?.[2] || '';
    let path = urlParts?.[1] || '/';
    const languagePrefix = this.getLanguagePrefixPattern();

    path = path.replace(languagePrefix, '') || '/';

    if (!path.startsWith('/')) {
      path = `/${path}`;
    }

    if (!this.shouldUseLanguagePrefix(lang)) {
      return `${path}${suffix}`;
    }

    return path === '/' ? `/${lang}${suffix}` : `/${lang}${path}${suffix}`;
  }

  private shouldUseLanguagePrefix(lang: string): boolean {
    const defaultLang = this.localize.parser.defaultLang || this.translate.getDefaultLang();

    return this.localize.settings.alwaysSetPrefix || lang !== defaultLang;
  }

  private getLanguagePrefixPattern(): RegExp {
    const locales = this.localize.parser.locales.map((locale) => this.escapeRegExp(locale));

    return new RegExp(`^/(${locales.join('|')})(?=/|$)`);
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private deepCopy<T>(value: T): T {
    if (value === null || typeof value !== 'object') {
      return value;
    }

    const output = Array.isArray(value) ? [] : {};

    Object.keys(value as Record<string, unknown>).forEach((key) => {
      (output as Record<string, unknown>)[key] = this.deepCopy((value as Record<string, unknown>)[key]);
    });

    return output as T;
  }

  ngOnInit(): void {}

  logout(): void {
    this.authService.logout();
  }

  public isSubscription$(): Observable<boolean> {
    return combineLatest([this.subscription$, this.user$])
        .pipe(
            map(([sub, user]) => {
              const canEdit = this.ability.can('update', 'Settings');

              if (!canEdit) return false;
              return !Boolean(sub);
            }),
        );
  }

  get isMarketplaceMode(): boolean {
   return Boolean(localStorage.getItem('DWallMarketplaceMode'))
  }
}
